import { AxiosError } from 'axios';
import { PaletteMode } from '@mui/material';
import { action, makeAutoObservable, observable } from 'mobx';
import { makePersistable } from 'mobx-persist-store';
import CustomLogger from '80.quickConnect.Core/logger/customLogger';
import RootStore from '30.quickConnect.Stores/RootStore';
import { API_GET_API_INFORMATION } from '40.quickConnect.DataAccess/axios/apiRoutes';
import IClientHTTP from '40.quickConnect.DataAccess/ClientHTTP/interface';
import { DateTimeExtension } from '80.quickConnect.Core/formatting/DateTimeExtension';
import { AppInformationResponse } from '30.quickConnect.Stores/RootStore/UserSettingsStore/Payloads/responses';
import { OrganizationalUnitInfo, QCErrorHTTP } from '90.quickConnect.Models/models';
import { LogLevel } from '90.quickConnect.Models/enums';
import { errorHandler } from '80.quickConnect.Core/helpers';
import getConfiguredLogLevel from '80.quickConnect.Core/helpers/getConfiguredLogLevel';

const DEBUG_VERBOSE_DURATION_MS = 24 * 60 * 60 * 1000; // 24h
const DEBUG_VERBOSE_DURATION_DEV_MS = 20 * 60 * 1000; // 20 min

class UserSettingsStore {
  // Tag
  private static readonly TAG = '30.quickConnect.Stores/RootStore/UserSettingsStore/index.ts';

  clientHTTP: IClientHTTP;

  formsAsTree = true;

  inboxesAsTree = true;

  inboxesAsAlphaNumericSort = true;

  declarationsAsTree = true;

  autoSwitchOU = false;

  currentOU: string | undefined;

  userOUs: OrganizationalUnitInfo[] = [];

  logger: CustomLogger;

  RootStore: RootStore;

  muiMode: PaletteMode = 'dark';

  syncDate = '';

  // Mode debug developpeur (persiste).
  qcDebugMode = false;

  // Niveau de log initial issu du .env, sauvegarde pour restauration a la desactivation.
  originalLogLevel: LogLevel;

  // Timestamp UNIX de la derniere activation du mode debug (persiste).
  debugEnableDate = 0;

  constructor(rootStore: RootStore, logger: CustomLogger, storageKey: string) {
    this.logger = logger;
    this.RootStore = rootStore;
    this.clientHTTP = rootStore.clientHTTP;
    makeAutoObservable(
      this,
      {
        formsAsTree: observable,
        inboxesAsTree: observable,
        inboxesAsAlphaNumericSort: observable,
        declarationsAsTree: observable,
        autoSwitchOU: observable,
        muiMode: observable,
        currentOU: observable,
        userOUs: observable,
        qcDebugMode: observable,
        originalLogLevel: observable,
        debugEnableDate: observable,

        switchMuiMode: action,
        setFormsAsTree: action,
        setInboxesAsTree: action,
        setInboxesAsAlphaNumericSort: action,
        setDeclarationsAsTree: action,
        setAutoSwitchOU: action,
        setCurrentOU: action,
        setUserOUs: action,
        hydrateQcDebugMode: action,
        setQcDebugMode: action,
        toggleQcDebugMode: action,
      },
      { autoBind: true },
    );

    // Capture du niveau de log d'origine.
    this.originalLogLevel = getConfiguredLogLevel();

    // makePersistable est async : hydrater apres resolution pour lire les valeurs restaurees.
    void makePersistable(this, {
      name: storageKey,
      properties: [
        'formsAsTree',
        'inboxesAsTree',
        'inboxesAsAlphaNumericSort',
        'declarationsAsTree',
        'autoSwitchOU',
        'muiMode',
        'syncDate',
        'qcDebugMode',
        'debugEnableDate',
      ],
      storage: window.localStorage,
    }).then(() => {
      // Les valeurs persistees sont disponibles : on evalue l'expiration et synchronise le logger.
      this.hydrateQcDebugMode();
    });
  }

  resetStore = (): void => {
    this.formsAsTree = true;
    this.inboxesAsTree = true;
    this.inboxesAsAlphaNumericSort = true;
    this.declarationsAsTree = true;
    this.autoSwitchOU = false;
    this.muiMode = 'dark';
    this.syncDate = '';
    this.setQcDebugMode(false);
    this.currentOU = undefined;
    this.userOUs = [];
  };

  hydrateQcDebugMode = (): void => {
    // Aucune activation persistee : on s'assure que l'etat est propre.
    if (!this.qcDebugMode || !this.debugEnableDate) {
      this.clearQcDebugMode();
      this.applyQcDebugMode(false);
      return;
    }

    const elapsed = Date.now() - this.debugEnableDate;
    const isDev = process.env.REACT_APP_ENV === 'dev';
    const maxDuration = isDev ? DEBUG_VERBOSE_DURATION_DEV_MS : DEBUG_VERBOSE_DURATION_MS;

    // Fenetre de validite depassee : desactivation automatique.
    if (elapsed > maxDuration) {
      this.clearQcDebugMode();
      this.applyQcDebugMode(false);
      return;
    }

    // Mode encore valide : on conserve la date d'activation d'origine.
    this.applyQcDebugMode(true);
  };

  setQcDebugMode = (enabled: boolean): void => {
    // Niveau deja Trace/Debug : le mode debug local est inutile.
    if (enabled && this.originalLogLevel <= LogLevel.Debug) {
      this.clearQcDebugMode();
      this.applyQcDebugMode(false);
      return;
    }

    if (enabled) {
      this.qcDebugMode = true;
      this.debugEnableDate = Date.now(); // Demarre une nouvelle fenetre de validite.
      this.applyQcDebugMode(true);
      return;
    }

    this.clearQcDebugMode();
    this.applyQcDebugMode(false);
  };

  private applyQcDebugMode = (enabled: boolean): void => {
    this.qcDebugMode = enabled;
    // Applique Debug si le niveau d'origine est plus restrictif, sinon restaure l'original.
    this.logger.logLevelNumber =
      enabled && this.originalLogLevel > LogLevel.Debug ? LogLevel.Debug : this.originalLogLevel;
  };

  private clearQcDebugMode = (): void => {
    this.qcDebugMode = false;
    this.debugEnableDate = 0;
  };

  toggleQcDebugMode = (): void => {
    this.setQcDebugMode(!this.qcDebugMode);
  };

  setSyncDate = (dt: Date) => {
    this.syncDate = DateTimeExtension.setISOFormat(dt);
  };

  switchMuiMode = (): void => {
    this.muiMode = this.muiMode === 'light' ? 'dark' : 'light';
  };

  setFormsAsTree = (formsAsTree: boolean): void => {
    this.formsAsTree = formsAsTree;
  };

  setInboxesAsTree = (inboxesAsTree: boolean): void => {
    this.inboxesAsTree = inboxesAsTree;
  };

  setCurrentOU = (ou: string) => (this.currentOU = ou);

  setUserOUs = (ous: OrganizationalUnitInfo[]) => (this.userOUs = ous);

  setInboxesAsAlphaNumericSort = (inboxesAsAlphaNumericSort: boolean): void => {
    this.inboxesAsAlphaNumericSort = inboxesAsAlphaNumericSort;
  };

  setDeclarationsAsTree = (declarationsAsTree: boolean): void => {
    this.declarationsAsTree = declarationsAsTree;
  };

  setAutoSwitchOU = (autoSwitchOU: boolean): void => {
    this.autoSwitchOU = autoSwitchOU;
  };

  async getApiInformation() {
    try {
      const result = await this.clientHTTP.get<AppInformationResponse>(
        this.RootStore.CommonStore.chooseBaseUrl(API_GET_API_INFORMATION),
        {
          withCredentials: true,
        },
      );

      return result.data.information;
    } catch (error) {
      if (QCErrorHTTP.isHttpError<AxiosError<AppInformationResponse>>(error)) {
        error.message = `Erreur sur la requête getAPIInformation: ErrorCode: ${error.response?.data.errorCode} - Message: ${error.response?.data.message}`;
      }
      errorHandler(UserSettingsStore.TAG, error, 'getApiInformation');
    }
  }
}
export default UserSettingsStore;
